import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, MoonIcon, PlusIcon, SunIcon, TruckIcon, VideoIcon } from "lucide-react";
import { toast } from "sonner";
import type { IconComponent } from "../../../ui/icon";
import { ProductImage } from "../../../ui/ProductImage";
import { useCatalog } from "../../../contexts/CatalogContext";
import { useCartActions, useOverlayActions } from "../../../contexts/StoreContext";
import { useSignedInAccount } from "../../../contexts/AccountContext";
import { useSkinPlan } from "../../../hooks/useSkinPlan";
import { founder } from "../../../data/content";
import { useSite } from "../../../contexts/SiteContext";
import { dayShort, refillLabel, refillState, runsOutBeforeDelivery, slotItems } from "../../../utils/account";
import { ease } from "../../../core/format/motion";
import { plural } from "../../../core/format/text";
import { isSoldOut } from "../../../core/rules/stock";
import { shopLabels } from "../../../data/shopLabels";
import { PlanItem, RoutineSlot } from "../../../core/types/account";
import { btnPrimary, btnSecondary } from "../../../ui/styles";
import { accountHeading } from "../../../ui/sections";
const slots: {
  key: RoutineSlot;
  label: string;
  icon: IconComponent;
}[] = [{
  key: 'ochtend',
  label: 'Ochtend',
  icon: SunIcon
}, {
  key: 'avond',
  label: 'Avond',
  icon: MoonIcon
}];
/** Vanaf dit uur opent het plan op de avondroutine: dan is dat wat je straks doet. */
const EVENING_FROM_HOUR = 15;
/** Onder dit percentage blijft er toch een streepje zichtbaar in de balk. */
const MIN_BAR_PERCENT = 4;
const defaultSlot = (): RoutineSlot => new Date().getHours() >= EVENING_FROM_HOUR ? 'avond' : 'ochtend';

/** De routine in de volgorde waarin ze het gebruikt. De kern van het dashboard. */
export function SkinPlanCard() {
  const {
    policy
  } = useSite();
  const {
    account
  } = useSignedInAccount();
  const {
    openBooking
  } = useOverlayActions();
  const {
    items: all,
    provisional,
    routine
  } = useSkinPlan();
  const reduce = useReducedMotion();
  const [slot, setSlot] = useState<RoutineSlot>(defaultSlot);
  const tabRefs = useRef<Record<RoutineSlot, HTMLButtonElement | null>>({
    ochtend: null,
    avond: null
  });
  const items = slotItems(all, slot);
  const subtitle = provisional ? 'Zo gebruik je wat je hebt besteld' : [routine?.name, account.plan.focus].filter(Boolean).join(' · ');
  const choose = (next: RoutineSlot) => {
    setSlot(next);
    tabRefs.current[next]?.focus();
  };
  const onTabKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      choose(slot === 'ochtend' ? 'avond' : 'ochtend');
    } else if (e.key === 'Home') {
      e.preventDefault();
      choose('ochtend');
    } else if (e.key === 'End') {
      e.preventDefault();
      choose('avond');
    }
  };
  return <section aria-labelledby="plan-title" className="bg-shell ring-1 ring-line" data-id="element-6128" data-mp="1oziay13k0vkq">
      <header className="flex flex-col gap-4 border-b border-line px-5 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-7 sm:py-6" data-id="element-6129" data-mp="1tx0aj0volyg2">
        <div className="min-w-0" data-id="element-6130" data-mp="1yv1tllt3xxrj">
          <h2 id="plan-title" className={accountHeading} data-id="element-6131" data-mp="23vpssurhpkwu">
            {provisional ? 'Jouw producten' : 'Jouw huidplan'}
          </h2>
          {subtitle && <p className="mt-1 text-[14px] text-muted" data-id="element-6132" data-mp="27wjgpo4xmzpq">{subtitle}</p>}
        </div>
        <div role="tablist" aria-label="Moment van de dag" className="relative flex shrink-0 self-start rounded-full bg-sand p-1 sm:self-auto" data-id="element-6133" data-mp="2cvmcehz0vtl1">
          {slots.map(s => {
          const on = slot === s.key;
          const count = slotItems(all, s.key).length;
          return <button key={s.key} ref={el => {
            tabRefs.current[s.key] = el;
          }} type="button" role="tab" id={`plan-tab-${s.key}`} aria-controls="plan-panel" aria-selected={on} aria-label={`${s.label}, ${plural(count, 'product', 'producten')}`} tabIndex={on ? 0 : -1} onKeyDown={onTabKey} onClick={() => setSlot(s.key)} className={`relative flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[14px] transition-colors duration-150 ease-soft ${on ? 'text-shell' : 'text-muted hover:text-ink'}`} data-id="element-6134" data-mp="2rlaf9nmur0av">
                {on && <motion.span layoutId="plan-slot" className="absolute inset-0 rounded-full bg-ink" transition={reduce ? {
              duration: 0
            } : {
              duration: 0.22,
              ease
            }} data-id="element-6135" data-mp="1y5lovnp61jj" />}
                <s.icon className="relative h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6136" data-mp="35fw5msrrq9u3" />
                <span className="relative" aria-hidden="true" data-id="element-6137" data-mp="3jevvg3r0ouru">
                  {s.label}
                </span>
                <span className={`relative text-[12px] tabular-nums ${on ? 'text-shell/70' : 'text-muted'}`} aria-hidden="true" data-id="element-6138" data-mp="3eglv7eqfr3zd">
                  {count}
                </span>
              </button>;
        })}
        </div>
      </header>

      <AnimatePresence mode="wait" initial={false} data-id="element-6139" data-mp="124rh8xygc61d">
        <motion.div key={slot} id="plan-panel" role="tabpanel" tabIndex={0} aria-labelledby={`plan-tab-${slot}`} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: slot === 'avond' ? 12 : -12
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-clay" data-id="element-6140" data-mp="127dosxlceljl">
          {items.length ? <ol className="divide-y divide-line" data-id="element-6141" data-mp="1pu291pxtqq3d">
              {items.map((item, i) => <PlanRow key={item.productId} item={item} step={i + 1} data-id="element-6142" data-mp="1b564i2uz7v7i" data-mpi={i} />)}
            </ol> : <p className="px-5 py-8 text-[15px] leading-relaxed text-muted sm:px-7" data-id="element-6143" data-mp="1g4adasdznqxb">
              {slot === 'ochtend' ? 'Je producten horen bij de avond. ’s Ochtends is water en je SPF genoeg.' : 'Je producten horen bij de ochtend. ’s Avonds reinig je mild en laat je je huid rusten.'}
            </p>}
        </motion.div>
      </AnimatePresence>

      {provisional ? <div className="flex flex-col gap-4 border-t border-line bg-sand/60 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7" data-id="element-6144" data-mp="2iezdalvct8vh">
          <p className="text-[14px] leading-relaxed text-ink" data-id="element-6145" data-mp="28ie37m4h97ks">
            Wil je dat {founder.firstName} je routine persoonlijk maakt? In {policy.consult.minutes} minuten kijkt ze wat je huid echt nodig heeft.
          </p>
          <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className={`${btnPrimary} shrink-0`} data-id="element-6146" data-mp="1zk256z1a77ib">
            <VideoIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6147" data-mp="1pso3qlprrxmj" />
            Plan een huidcheck
          </button>
        </div> : account.plan.note && <p className="border-t border-line px-5 py-4 text-[14px] leading-relaxed text-muted sm:px-7" data-id="element-6148" data-mp="1kv5yb5ayo0ja">
            <span className="text-ink" data-id="element-6149" data-mp="1gr6m2ywprnvq">Notitie van {founder.firstName}:</span> {account.plan.note}
          </p>}
    </section>;
}
// De actie per product: altijd de gedeelde knop (ui/styles.ts), ± 46px hoog.
const pill = btnPrimary;
function PlanRow({
  item,
  step
}: {
  item: PlanItem;
  step: number;
}) {
  const {
    account,
    subscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const {
    add
  } = useCartActions();
  const reduce = useReducedMotion();
  const product = productById(item.productId);
  if (!product) return null;
  const toggleExtra = () => subscription({
    type: 'toggleExtra',
    productId: item.productId
  });
  const sub = account.subscription;
  const active = sub?.status === 'actief';
  const inSub = Boolean(sub?.items.some(x => x.productId === item.productId));
  const extra = Boolean(sub?.extras.includes(item.productId));
  const refill = refillState(item);
  const low = refill.level !== 'ruim';
  const soldOut = isSoldOut(product);
  const percent = Math.round(refill.left * 100);
  let action: ReactNode = null;
  if (low && active && sub && inSub && runsOutBeforeDelivery(item, sub)) {
    action = <button type="button" onClick={async () => {
      if (await subscription({
        type: 'sendEarlier'
      })) toast.success('Je levering komt eerder', {
        description: 'We pakken hem morgen voor je in.'
      });
    }} className={`${pill} bg-ink text-shell hover:bg-clay`} data-id="element-6150" data-mp="1jebkwe44ljds">
        <TruckIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-6151" data-mp="1obk4mj4x9vh5" />
        Stuur levering eerder
      </button>;
  } else if (low && active && sub && inSub) {
    action = <span className="inline-flex items-center gap-1.5 text-[13px] text-sage" data-id="element-6152" data-mp="1xdtgpdfu9wwn">
        <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-6153" data-mp="2c27om2s44bk2" />
        Komt mee op {dayShort(sub.nextDelivery)}
      </span>;
  } else if (low && soldOut) {
    // Bijbestellen of meesturen kan niet: eerlijk zeggen in plaats van een knop die een foutmelding geeft.
    action = <span className="text-[13px] text-clay" data-id="element-6154" data-mp="2l0jicdo9zpgq">{shopLabels.soldOutShort}</span>;
  } else if (low && active && sub) {
    action = <button type="button" onClick={toggleExtra} aria-pressed={extra} aria-label={extra ? `${product.name} gaat mee op ${dayShort(sub.nextDelivery)}` : `${product.name} toevoegen aan je levering`} className={extra ? btnSecondary : btnPrimary} data-id="element-6155" data-mp="2zp301m2ezgni">
        {extra ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-6156" data-mp="2qomv0860w1gj" /> : <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-6157" data-mp="2up6u3ha5pf5b" />}
        {extra ? `Mee op ${dayShort(sub.nextDelivery)}` : 'Voeg toe aan levering'}
      </button>;
  } else if (low) {
    action = <button type="button" onClick={() => add(product)} aria-label={`${product.name} bijbestellen`} className={`${pill} bg-ink text-shell hover:bg-clay`} data-id="element-6158" data-mp="2lqld3qkc286n">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-6159" data-mp="2bzqqum7378l7" />
        Bijbestellen
      </button>;
  }
  return <li className="grid grid-cols-[auto_56px_minmax(0,1fr)] gap-x-4 gap-y-3 px-5 py-5 sm:grid-cols-[auto_64px_minmax(0,1fr)_auto] sm:items-center sm:px-7" data-id="element-6160" data-mp="1y1aas4nc45qe">
      {/* Het nummer is de volgorde van de <ol>; een schermlezer noemt die al. */}
      <span className="pt-1 font-display text-[18px] leading-none text-clay sm:pt-0" aria-hidden="true" data-id="element-6161" data-mp="38jv1wh8q34qp">
        {step}
      </span>
      <Link to={`/webshop/${product.id}`} className="block aspect-square bg-sand" tabIndex={-1} aria-hidden="true" data-id="element-6162" data-mp="2zhc1e3set47m">
        <ProductImage src={product.image} alt="" className="h-full w-full p-1.5" data-id="element-6163" data-mp="34fdjomi0gzi1" />
      </Link>
      <div className="min-w-0" data-id="element-6164" data-mp="3s57xp7e2gu3v">
        <p className="text-[13px] text-muted" data-id="element-6165" data-mp="3s69jo6wmf10d">
          {product.step ?? product.category}
          {item.frequency && <span className="text-clay" data-id="element-6166" data-mp="15jy43c4mgg"> · {item.frequency}</span>}
        </p>
        <Link to={`/webshop/${product.id}`} className="mt-0.5 block text-[16px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-6167" data-mp="3if52r1ndx62w">
          {product.name}
        </Link>
        {item.instruction && <p className="mt-1 text-[14px] leading-relaxed text-muted" data-id="element-6168" data-mp="eyfwlgrprydr">{item.instruction}</p>}
      </div>

      <div className="col-span-3 flex flex-wrap items-center justify-between gap-3 sm:col-span-1 sm:w-48 sm:flex-col sm:items-end sm:justify-center" data-id="element-6169" data-mp="jqpsrejf9uoh">
        <div className="w-32 sm:w-full" data-id="element-6170" data-mp="p34qw6gyssuv">
          <div className="h-1 overflow-hidden rounded-full bg-sand" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={`${product.name}: nog ongeveer ${percent}% over`} data-id="element-6171" data-mp="p2d2bflsbve3">
            <motion.div initial={reduce ? false : {
            width: 0
          }} animate={{
            width: `${Math.max(MIN_BAR_PERCENT, percent)}%`
          }} transition={{
            duration: 0.3,
            ease
          }} className={`h-full rounded-full ${refill.level === 'ruim' ? 'bg-sage' : 'bg-clay'}`} data-id="element-6172" data-mp="xvzvqz2ismk4" />
          </div>
          <p className={`mt-1.5 text-[12px] sm:text-right ${low ? 'text-clay' : 'text-muted'}`} data-id="element-6173" data-mp="f4yo7mnc3ioy">{refillLabel(refill)}</p>
        </div>
        {action}
      </div>
    </li>;
}